import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  BookOpen, Plane, User, ChevronRight, LogOut, 
  Award, Flame, Trophy, Globe, GraduationCap, Settings,
  Mic, PenTool, Headphones, Video, X, ArrowLeft,
  CheckCircle, Clock, Star, TrendingUp, Calendar, Target, Lock, Brain, Sparkles
} from 'lucide-react';
import axios from 'axios';
import KariyerLogo from '../components/KariyerLogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MainMenu()     {
  const { user, setUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [gamificationStats, setGamificationStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activePanel, setActivePanel] = useState(null); // 'derslerim' | 'ilerleme' | null
  const [lessonsData, setLessonsData] = useState(null);
  const [progressData, setProgressData] = useState(null);

  useEffect(() =>     {
    fetchStats();
    fetchGamificationStats();
  }, []);

  const fetchGamificationStats = async () =>     {
    try     {
      const response = await axios.get(`${API}/gamification/profile`, { withCredentials: true });
      setGamificationStats(response.data);
    } catch (error)     {
      console.error('Error fetching gamification stats:', error);
    }
  };

  const fetchStats = async () =>     {
    try     {
      const [englishStats, cabinStats] = await Promise.all([
        axios.get(`${API}/kariyer/stats`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/cabin-training/stats`, { withCredentials: true }).catch(() => ({ data: null }))
      ]);
      setStats(    {
        english: englishStats.data,
        cabin: cabinStats.data
      });
    } catch (error)     {
      console.error('Error fetching stats:', error);
    } finally     {
      setLoading(false);
    }
  };

  const handleLogout = async () =>     {
    try     {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error)     {
      console.log('Logout error:', error);
    }
    localStorage.removeItem('session_token');
    setUser(null);
    navigate('/');
  };

  const menuItems = [
    {
      id: 'english',
      title: 'İngilizce Eğitimi',
      subtitle: 'Havacılık İngilizcesi A0-C1',
      description: 'Seviye tespit sınavı ile başla',
      icon: Globe,
      color: 'from-blue-500 to-indigo-600',
      bgColor: 'bg-blue-50',
      iconColor: 'text-blue-600',
      route: '/english-training',
      stats: stats?.english ? `${stats.english.miles || 0} Mil` : null
    },
    {
      id: 'cabin',
      title: 'Kabin Memurluğu Eğitimi',
      subtitle: 'Türkçe Mesleki Eğitim',
      description: 'Kabin ekibi olmak için gereken her şey',
      icon: Plane,
      color: 'from-emerald-500 to-teal-600',
      bgColor: 'bg-emerald-50',
      iconColor: 'text-emerald-600',
      route: '/cabin-training',
      stats: stats?.cabin ? `${stats.cabin.completed || 0} Tamamlandı` : null
    },
    {
      id: 'video-lessons',
      title: 'Videolu Dersler',
      subtitle: 'Vimeo Eğitim Videoları',
      description: 'Video derslerle öğrenmeye devam edin',
      icon: Video,
      color: 'from-purple-500 to-violet-600',
      bgColor: 'bg-purple-50',
      iconColor: 'text-purple-600',
      route: '/video-lessons',
      stats: null
    },
    {
      id: 'interview-prep',
      title: 'Mülakat Hazırlık Eğitimi',
      subtitle: 'THY, Emirates, Pegasus ve daha fazlası',
      description: 'Kapsamlı mülakat hazırlık rehberi',
      icon: Target,
      color: 'from-rose-500 to-pink-600',
      bgColor: 'bg-rose-50',
      iconColor: 'text-rose-600',
      route: '/interview-prep',
      stats: null,
      locked: false,
      premium: false
    },

    {
      id: 'interview-practice-hub',
      title: 'İnteraktif Pratik Merkezi',
      subtitle: 'Quiz, Flashcard, Checklist',
      description: 'Çeşitli pratik yöntemleri',
      icon: Sparkles,
      color: 'from-cyan-500 to-blue-600',
      bgColor: 'bg-cyan-50',
      iconColor: 'text-cyan-600',
      route: '/interview-practice-hub',
      stats: null,
      locked: false,
      premium: false
    },
    {
      id: 'ai-voice-interview',
      title: 'Sesli Mülakat',
      subtitle: 'Pratik Yap Öğren',
      description: 'Sesli mülakat pratiği yapın',
      icon: Mic,
      color: 'from-purple-500 to-indigo-600',
      bgColor: 'bg-purple-50',
      iconColor: 'text-purple-600',
      route: '/ai-voice-interview',
      stats: null,
      locked: false,
      premium: false
    },
    {
      id: 'video-interview',
      title: 'Videolu Mülakat',
      subtitle: 'Kendinizi Kaydedin',
      description: 'Video ile mülakat pratiği',
      icon: Video,
      color: 'from-amber-500 to-orange-600',
      bgColor: 'bg-amber-50',
      iconColor: 'text-amber-600',
      route: '/video-interview',
      stats: null,
      locked: false,
      premium: false
    },
    {
      id: 'psychology-tests',
      title: 'Psikolojik Testler',
      subtitle: 'Kişilik & Yetenek Analizi',
      description: 'Kendinizi keşfedin',
      icon: Brain,
      color: 'from-indigo-500 to-purple-600',
      bgColor: 'bg-indigo-50',
      iconColor: 'text-indigo-600',
      route: '/psychology-tests',
      stats: null,
      locked: false,
      premium: false
    },
    {
      id: 'podcasts',
      title: 'Podcastler',
      subtitle: 'Sesli Eğitim İçerikleri',
      description: 'Mülakat ipuçları ve kariyer tavsiyeleri',
      icon: Headphones,
      color: 'from-purple-500 to-pink-600',
      bgColor: 'bg-purple-50',
      iconColor: 'text-purple-600',
      route: '/podcasts',
      stats: null,
      locked: false,
      premium: false
    }
  ];

  if (loading)     {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-500 to-blue-600 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-bounce text-6xl mb-4">✈️</div>
          <p className="text-white font-medium">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-4 pt-6 pb-12 rounded-b-[2rem]">
        <div className="max-w-4xl mx-auto">
          {/* Top Bar */}
          <div className="flex items-center justify-between mb-6">
            <KariyerLogo size="default" variant="white" />
            <div className="flex items-center gap-2">
              {user?.role === 'admin' && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => navigate('/admin')}
                  className="text-white/80 hover:text-white hover:bg-white/10"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Admin
                </Button>
              )}
              <Button 
                variant="ghost" 
                size="sm"
                onClick={handleLogout}
                className="text-white/80 hover:text-white hover:bg-white/10"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Çıkış
              </Button>
            </div>
          </div>

          {/* Welcome */}
          <div className="text-center text-white">
            <p className="text-blue-100 text-sm mb-1">Hoş geldin 👋</p>
            <h1 className="text-2xl font-bold">{user?.name || 'Öğrenci'}</h1>
          </div>

          {/* Gamification İstatistikleri - Header içinde */}
          <div className="mt-4 grid grid-cols-4 gap-2">
            <div className="bg-white/20 backdrop-blur rounded-xl p-3 text-center">
              <span className="text-xl">✈️</span>
              <p className="text-lg font-bold text-white">{gamificationStats?.total_miles || 0}</p>
              <p className="text-xs text-blue-100">Mil</p>
            </div>
            <div className="bg-white/20 backdrop-blur rounded-xl p-3 text-center">
              <Flame className="w-5 h-5 text-orange-300 mx-auto" />
              <p className="text-lg font-bold text-white">{gamificationStats?.streak?.current || 0}</p>
              <p className="text-xs text-blue-100">Gün Serisi</p>
            </div>
            <div className="bg-white/20 backdrop-blur rounded-xl p-3 text-center">
              <Trophy className="w-5 h-5 text-yellow-300 mx-auto" />
              <p className="text-lg font-bold text-white">{gamificationStats?.career_level?.level || 1}</p>
              <p className="text-xs text-blue-100">Seviye</p>
            </div>
            <div className="bg-white/20 backdrop-blur rounded-xl p-3 text-center">
              <Award className="w-5 h-5 text-purple-300 mx-auto" />
              <p className="text-lg font-bold text-white">{gamificationStats?.collected_tickets?.length || 0}/{gamificationStats?.total_routes || 10}</p>
              <p className="text-xs text-blue-100">Rota</p>
            </div>
          </div>
        </div>
      </div>

      {/* Menu Cards */}
      <div className="max-w-4xl mx-auto px-4 -mt-6">
        {/* Gamification Kartları - Üstte */}
        <div className="mb-6">
          <div className="grid grid-cols-3 gap-3">
            <Card 
              className="border-0 shadow-lg bg-gradient-to-br from-green-400 to-emerald-500 cursor-pointer hover:shadow-xl hover:-translate-y-1 transition-all"
              onClick={() => navigate('/profile')}
            >
              <CardContent className="p-4 text-center text-white">
                <span className="text-2xl">👤</span>
                <p className="text-xs font-bold mt-1">Profilim</p>
              </CardContent>
            </Card>
            
            <Card 
              className="border-0 shadow-lg bg-gradient-to-br from-amber-400 to-orange-500 cursor-pointer hover:shadow-xl hover:-translate-y-1 transition-all"
              onClick={() => navigate('/leaderboard')}
            >
              <CardContent className="p-4 text-center text-white">
                <span className="text-2xl">🏆</span>
                <p className="text-xs font-bold mt-1">Sıralama</p>
              </CardContent>
            </Card>
            
            <Card 
              className="border-0 shadow-lg bg-gradient-to-br from-purple-400 to-violet-500 cursor-pointer hover:shadow-xl hover:-translate-y-1 transition-all"
              onClick={() => navigate('/career-levels')}
            >
              <CardContent className="p-4 text-center text-white">
                <span className="text-2xl">🎖️</span>
                <p className="text-xs font-bold mt-1">Seviyem</p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Eğitim Menüleri */}
        <div className="space-y-4">
          {menuItems.map((item) =>     {
            const Icon = item.icon;
            const isLocked = item.locked === true;
            
            return (
              <Card 
                key={item.id}
                className={`overflow-hidden transition-all duration-300 border-0 shadow-md $    {
                  isLocked 
                    ? 'opacity-75 cursor-not-allowed' 
                    : 'cursor-pointer hover:shadow-lg'
                }`}
                onClick={() => !isLocked && navigate(item.route)}
              >
                <CardContent className="p-0">
                  <div className="flex items-center relative">
                    {/* Locked Overlay */}
                    {isLocked && (
                      <div className="absolute inset-0 bg-gray-900/10 z-10 flex items-center justify-end pr-4">
                        <div className="bg-gray-800 text-white px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                          🔒 Kilitli
                        </div>
                      </div>
                    )}
                    
                    {/* Icon Section */}
                    <div className={`w-24 h-28 bg-gradient-to-br ${isLocked ? 'from-gray-400 to-gray-500' : item.color} flex items-center justify-center relative`}>
                      <Icon className="w-10 h-10 text-white" />
                      {item.premium && (
                        <div className="absolute top-2 right-2 bg-amber-400 text-amber-900 text-[10px] font-bold px-1.5 py-0.5 rounded">
                          PRO
                        </div>
                      )}
                    </div>
                    
                    {/* Content Section */}
                    <div className="flex-1 p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className={`font-bold text-lg ${isLocked ? 'text-gray-500' : 'text-gray-900'}`}>{item.title}</h3>
                          <p className={`text-sm mt-0.5 ${isLocked ? 'text-gray-400' : 'text-gray-500'}`}>{item.subtitle}</p>
                          <p className={`text-xs mt-1 ${isLocked ? 'text-amber-600 font-medium' : 'text-gray-400'}`}>{item.description}</p>
                        </div>
                        <ChevronRight className={`w-5 h-5 mt-1 ${isLocked ? 'text-gray-300' : 'text-gray-400'}`} />
                      </div>
                      
                      {item.stats && !isLocked && (
                        <div className="mt-2">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${item.bgColor} ${item.iconColor}`}>
                            <Award className="w-3 h-3 mr-1" />
                            {item.stats}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Footer */}
        <div className="mt-8 text-center pb-8">
          <p className="text-xs text-gray-400">
            © 2025 Kariyer Online. Tüm Hakları Saklıdır.
          </p>
        </div>
      </div>
    </div>
  );
}
